import React, { useEffect, useState } from "react";
import Shell from "@/components/layout/Shell";
import { PageHeader, PrimaryButton, GhostButton, Field, inputCls } from "@/components/common";
import { api, readImageDataUrl } from "@/lib/api";
import { toast } from "sonner";
import { Building2, FileSignature, Upload, X, Plus, Trash2, ArrowUp, ArrowDown, RotateCcw, Copy } from "lucide-react";

const Card = ({ icon: Icon, title, subtitle, children, action }) => (
  <section className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6 md:p-8">
    <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#F97316]/10 flex items-center justify-center"><Icon className="w-5 h-5 text-[#F97316]" strokeWidth={1.75}/></div>
        <div>
          <h2 className="text-lg font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>{title}</h2>
          {subtitle && <p className="text-xs text-[#9CA3AF]">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
    {children}
  </section>
);

const ImageField = ({ label, value, onChange, hint, testId }) => {
  const pick = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    try { onChange(await readImageDataUrl(file)); } catch (err) { toast.error(err.message); }
  };
  return (
    <div>
      <span className="block text-xs font-medium text-[#9CA3AF] mb-1.5">{label}</span>
      <div className="flex items-center gap-3">
        <div className="w-40 h-20 rounded-xl border border-dashed border-[#262A33] bg-white/95 flex items-center justify-center overflow-hidden">
          {value ? <img src={value} alt={label} className="max-w-full max-h-full object-contain"/> : <span className="text-[11px] text-[#6B7280]">Sin imagen</span>}
        </div>
        <div className="flex flex-col gap-2">
          <label className="inline-flex items-center gap-2 cursor-pointer bg-transparent border border-[#262A33] hover:bg-[#1A1D24] text-[#F5F5F7] font-medium rounded-xl px-3 py-2 text-sm transition-colors">
            <Upload className="w-4 h-4"/> Subir PNG/JPG
            <input data-testid={testId} type="file" accept="image/png,image/jpeg" className="hidden" onChange={pick}/>
          </label>
          {value && <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1 text-xs text-[#9CA3AF] hover:text-red-400"><X className="w-3.5 h-3.5"/> Quitar</button>}
        </div>
      </div>
      {hint && <span className="block text-[11px] text-[#6B7280] mt-1">{hint}</span>}
    </div>
  );
};

const AgencyForm = ({ settings, setSettings }) => {
  const [saving, setSaving] = useState(false);
  const f = (k) => ({ value: settings[k] || "", onChange: (e) => setSettings({ ...settings, [k]: e.target.value }), className: inputCls });
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { const { data } = await api.put("/settings/agency", settings); setSettings(data); toast.success("Datos de la agencia guardados"); }
    catch (err) { toast.error(err?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ImageField label="Logo (para los PDFs)" value={settings.logo_data_url} onChange={v => setSettings({ ...settings, logo_data_url: v })} hint="Se muestra en la cabecera de cotizaciones, contratos y cuentas de cobro." testId="logo-file-input"/>
        <ImageField label="Firma digitalizada" value={settings.signature_data_url} onChange={v => setSettings({ ...settings, signature_data_url: v })} hint="Opcional. Aparece sobre la línea de firma del representante legal." testId="signature-file-input"/>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Nombre comercial"><input data-testid="agency-brand-input" {...f("brand")}/></Field>
        <Field label="Razón social *"><input data-testid="agency-name-input" required {...f("name")}/></Field>
        <Field label="NIT *"><input required {...f("nit")}/></Field>
        <Field label="Ciudad"><input {...f("city")}/></Field>
        <Field label="Representante legal *"><input required {...f("legal_rep")}/></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Cédula"><input {...f("legal_rep_id")}/></Field>
          <Field label="Expedida en"><input {...f("legal_rep_id_city")}/></Field>
        </div>
        <Field label="Dirección"><input {...f("address")}/></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Email"><input {...f("email")}/></Field>
          <Field label="Teléfono"><input {...f("phone")}/></Field>
        </div>
      </div>
      <div>
        <div className="text-xs font-semibold text-[#F97316] uppercase tracking-wider mb-3">Datos bancarios (cuentas de cobro)</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Field label="Banco"><input data-testid="agency-bank-input" {...f("bank_name")}/></Field>
          <Field label="Tipo de cuenta"><input {...f("bank_account_type")}/></Field>
          <Field label="Número de cuenta"><input {...f("bank_account")}/></Field>
          <Field label="Titular"><input {...f("bank_holder")}/></Field>
        </div>
      </div>
      <Field label="Nota tributaria por defecto (cuenta de cobro)"><textarea rows={3} {...f("cc_note")}/></Field>
      <Field label="Condiciones por defecto (cotización)" hint="Una condición por línea."><textarea rows={3} {...f("quote_terms")}/></Field>
      <div className="flex justify-end"><PrimaryButton data-testid="save-agency-btn" type="submit" disabled={saving}>{saving ? "Guardando…" : "Guardar datos"}</PrimaryButton></div>
    </form>
  );
};

const TemplateEditor = ({ tpl, setTpl }) => {
  const [saving, setSaving] = useState(false);
  const clauses = tpl.clauses || [];
  const setClause = (i, patch) => setTpl({ ...tpl, clauses: clauses.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= clauses.length) return;
    const arr = [...clauses]; [arr[i], arr[j]] = [arr[j], arr[i]]; setTpl({ ...tpl, clauses: arr });
  };
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/settings/contract-template", { title: tpl.title, intro: tpl.intro, clauses, closing: tpl.closing }); setTpl(data); toast.success("Plantilla guardada"); }
    catch { toast.error("No se pudo guardar la plantilla"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("¿Restaurar la plantilla original? Perderás tus cambios.")) return;
    const { data } = await api.post("/settings/contract-template/reset"); setTpl(data); toast.success("Plantilla restaurada");
  };
  const copyVar = (v) => { navigator.clipboard?.writeText(`{{${v}}}`); toast.success(`{{${v}}} copiado`); };

  return (
    <div className="space-y-5">
      <div className="bg-[#0B0D12] border border-[#262A33] rounded-xl p-4">
        <div className="text-xs text-[#9CA3AF] mb-2">Variables disponibles (clic para copiar). Se reemplazan con los datos del contrato al generar el PDF:</div>
        <div className="flex flex-wrap gap-1.5">
          {(tpl.variables || []).map(v => (
            <button key={v} type="button" onClick={() => copyVar(v)} className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-md bg-[#F97316]/10 text-[#F97316] hover:bg-[#F97316]/20 transition-colors">
              <Copy className="w-3 h-3"/>{`{{${v}}}`}
            </button>
          ))}
        </div>
      </div>
      <Field label="Título"><textarea data-testid="tpl-title-input" rows={2} className={inputCls} value={tpl.title || ""} onChange={e => setTpl({ ...tpl, title: e.target.value })}/></Field>
      <Field label="Encabezado (partes del contrato)"><textarea rows={5} className={inputCls} value={tpl.intro || ""} onChange={e => setTpl({ ...tpl, intro: e.target.value })}/></Field>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-[#9CA3AF]">Cláusulas ({clauses.length})</span>
          <button type="button" data-testid="tpl-add-clause" onClick={() => setTpl({ ...tpl, clauses: [...clauses, { title: "CLÁUSULA NUEVA:", body: "" }] })} className="inline-flex items-center gap-1 text-xs text-[#F97316] hover:text-[#EA580C] font-medium"><Plus className="w-3.5 h-3.5"/> Agregar cláusula</button>
        </div>
        {clauses.map((c, i) => (
          <div key={i} data-testid={`tpl-clause-${i}`} className="bg-[#0B0D12] border border-[#262A33] rounded-xl p-4 space-y-2">
            <div className="flex gap-2 items-center">
              <span className="text-[11px] font-mono text-[#6B7280] w-6">{i + 1}</span>
              <input className={inputCls} value={c.title} onChange={e => setClause(i, { title: e.target.value })} placeholder="CLÁUSULA …: TÍTULO."/>
              <button type="button" onClick={() => move(i, -1)} className="p-1.5 rounded-lg text-[#9CA3AF] hover:bg-[#262A33]" aria-label="Subir"><ArrowUp className="w-4 h-4"/></button>
              <button type="button" onClick={() => move(i, 1)} className="p-1.5 rounded-lg text-[#9CA3AF] hover:bg-[#262A33]" aria-label="Bajar"><ArrowDown className="w-4 h-4"/></button>
              <button type="button" onClick={() => setTpl({ ...tpl, clauses: clauses.filter((_, idx) => idx !== i) })} className="p-1.5 rounded-lg text-[#6B7280] hover:text-red-400 hover:bg-red-500/10" aria-label="Eliminar"><Trash2 className="w-4 h-4"/></button>
            </div>
            <textarea rows={Math.min(10, Math.max(3, Math.ceil((c.body || "").length / 110)))} className={`${inputCls} text-xs leading-relaxed`} value={c.body} onChange={e => setClause(i, { body: e.target.value })}/>
          </div>
        ))}
      </div>
      <Field label="Cierre y firma"><textarea rows={2} className={inputCls} value={tpl.closing || ""} onChange={e => setTpl({ ...tpl, closing: e.target.value })}/></Field>
      <div className="flex justify-between gap-2 flex-wrap">
        <GhostButton type="button" data-testid="tpl-reset-btn" onClick={reset}><RotateCcw className="w-4 h-4"/> Restaurar original</GhostButton>
        <PrimaryButton data-testid="save-template-btn" type="button" onClick={save} disabled={saving}>{saving ? "Guardando…" : "Guardar plantilla"}</PrimaryButton>
      </div>
    </div>
  );
};

export default function Configuracion() {
  const [settings, setSettings] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [section, setSection] = useState("agencia");

  useEffect(() => {
    Promise.all([api.get("/settings/agency"), api.get("/settings/contract-template")])
      .then(([s, t]) => { setSettings(s.data); setTpl(t.data); })
      .catch(() => toast.error("No se pudo cargar la configuración"));
  }, []);

  return (
    <Shell>
      <PageHeader title="Configuración" subtitle="Datos de tu agencia y plantilla de contrato que se usan en todos los documentos PDF."/>
      <div className="mb-6 inline-flex items-center rounded-xl border border-[#262A33] bg-[#15171E] overflow-hidden">
        {[["agencia", "Datos de la agencia"], ["plantilla", "Plantilla de contrato"]].map(([k, l], i) => (
          <button key={k} data-testid={`config-tab-${k}`} onClick={() => setSection(k)} className={`px-4 py-2.5 text-sm font-medium transition-colors ${i > 0 ? "border-l border-[#262A33]" : ""} ${section === k ? "bg-[#F97316]/10 text-[#F97316]" : "text-[#9CA3AF] hover:text-[#F5F5F7]"}`}>{l}</button>
        ))}
      </div>
      {!settings || !tpl ? <div className="text-[#9CA3AF]">Cargando…</div> : section === "agencia" ? (
        <Card icon={Building2} title="Datos de la agencia" subtitle="Razón social, NIT, contacto, banco, logo y firma.">
          <AgencyForm settings={settings} setSettings={setSettings}/>
        </Card>
      ) : (
        <Card icon={FileSignature} title="Plantilla de contrato" subtitle="Edita el texto de las cláusulas. Usa variables para los datos que cambian por cliente.">
          <TemplateEditor tpl={tpl} setTpl={setTpl}/>
        </Card>
      )}
    </Shell>
  );
}
